import type { ParticleSystemGradient } from "@/types";

/**
 * Performs linear interpolation between points
 *
 * @param gradient - The value along the gradient to interpolate at
 * @param stops - Array of { gradient, factor } pairs
 * @returns The interpolated factor value at gradient
 */
export const linearInterpolation = (
  gradient: number,
  points: ParticleSystemGradient,
) => {
  if (points.length === 0) {
    throw new Error("At least one point is required");
  }

  if (points.length === 1) {
    return points[0].factor1;
  }

  // If gradient is before the first point, return first factor value
  if (gradient <= points[0].gradient) {
    return points[0].factor1;
  }

  // If gradient is after the last point, return last factor value
  if (gradient >= points[points.length - 1].gradient) {
    return points[points.length - 1].factor1;
  }

  // Find the two points to interpolate between
  for (let i = 0; i < points.length - 1; i++) {
    const { gradient: gradient1, factor1: factor1 } = points[i];
    const { gradient: gradient2, factor1: factor2 } = points[i + 1];

    if (gradient1 === gradient2) {
      return factor1;
    }

    if (gradient >= gradient1 && gradient <= gradient2) {
      // Linear interpolation formula: y = y1 + (y2 - y1) * (x - x1) / (x2 - x1)
      const t = (gradient - gradient1) / (gradient2 - gradient1);
      return factor1 + (factor2 - factor1) * t;
    }
  }

  return points[points.length - 1].factor1;
};
